import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { rem, rgba } from 'polished';
import {
  FlexRowCenterVertical,
  FlexColCenter,
} from '../../Defaults/Flexbox/Flexbox';
import { __, __r } from 'react-i18n';
import {
  ZlataPriadkaIconType as IconType,
  ZlataPriadkaSvgIcon as SvgIcon,
} from '../../..';
import { NotFound } from '../General/NotFound';
import { getArticleDetailInfo } from '../../../utilities/selectors';
import { TextP } from '../../Defaults/Text';
import { Modal } from '../../Defaults/Modal/Modal';
import { ImageGallery } from '../Product/ImageGallery';
import { getImagePath, formatDateFromString } from '../../../utilities';
import { TextLink } from '../General/Text';

interface Props {
  article: any;
  photogallery?: any;
  apiUrl: string;
  author: any;
  shareComponent: React.ReactNode;
  fromSitemap: boolean;
}

function Article({
  article,
  photogallery,
  apiUrl,
  author,
  shareComponent,
  fromSitemap,
}: Props) {
  if (!article) {
    return <NotFound text={__('Tento článok nebolo možné nájsť')} />;
  }
  const { title, date, content, downloads, lastUpdate } = getArticleDetailInfo(
    article,
  );

  const [imageModalVisible, setImageModalVisible] = React.useState(false);
  const [modalCurrentImage, setModalCurrentImage] = React.useState(0);
  const placeholderImgSrc = '/images/zlatapriadka/placeholder.svg';

  const fullscreenImages =
    photogallery &&
    photogallery.map(doc => {
      const img = getImagePath(apiUrl, doc.document_path, {
        width: 800,
        height: 600,
      });
      const thumbImg = getImagePath(apiUrl, doc.document_path, {
        width: 150,
        height: 150,
      });
      return {
        original: img,
        thumbnail: thumbImg,
        name: '',
      };
    });

  const closeImageModal = () => {
    setImageModalVisible(false);
  };

  const setCurrentImage = c => {
    setModalCurrentImage(c);
  };

  const openModal = (openOnIndex: number = 0) => {
    setImageModalVisible(true);
    setModalCurrentImage(openOnIndex);
  };

  return (
    <MainContent>
      {!fromSitemap && (
        <BackLink
          to={__r('routes:blog', '/blog')}
          title="Späť na zoznam článkov"
        >
          <SvgIcon
            icon={IconType.leftArrow}
            cursor="pointer"
            width={16}
            height={16}
            viewBox="0 0 16 16"
            marginRight={8}
          />
          {__('Späť na zoznam článkov')}
        </BackLink>
      )}
      <CmsTitle fromSitemap={fromSitemap}>{title}</CmsTitle>
      {author && (
        <AuthorShareWrapper>
          <ArticleInfoWrapper>
            <AuthorPhoto
              alt={`Fotka autora ${author.name}`}
              src={
                author.image
                  ? getImagePath(
                      apiUrl,
                      author.image,
                      {
                        width: 448,
                        height: 336,
                      },
                      false,
                      false,
                      true,
                    )
                  : placeholderImgSrc
              }
            />
            <InfoCover>
              <AuthorName>{author.name}</AuthorName>
              <Date>
                <CreatedDate>{formatDateFromString(date)}</CreatedDate>
                <LastUpdate>{`(${__(
                  'Aktualizované',
                )} ${lastUpdate})`}</LastUpdate>
              </Date>
            </InfoCover>
          </ArticleInfoWrapper>
          {!fromSitemap && shareComponent}
        </AuthorShareWrapper>
      )}
      <Body
        dangerouslySetInnerHTML={{
          __html: typeof content === 'object' ? '' : content,
        }}
      />
      <GalleryWrapper>
        {fullscreenImages &&
          fullscreenImages.map((image, i) => {
            if (i < 6) {
              return (
                <Thumb
                  src={image.thumbnail}
                  alt={`Obrázok`}
                  // tslint:disable-next-line:jsx-no-lambda
                  onClick={() => openModal(i)}
                />
              );
            }
            if (i === 6) {
              return (
                <MoreThumb
                  // tslint:disable-next-line:jsx-no-lambda
                  onClick={() => openModal(i)}
                >
                  ...
                </MoreThumb>
              );
            }
            return null;
          })}
      </GalleryWrapper>
      {downloads && downloads.length > 0 ? <h5>{__('Na stiahnutie:')}</h5> : ''}
      {downloads &&
        downloads.map(doc => {
          return (
            <>
              <DownloadLink
                href={`${process.env.REACT_APP_API_BASE_URL}/file?query=${doc.document_path}`}
                target="_blank"
              >
                {doc.document_description} {doc.document_name}
              </DownloadLink>
              <br />
            </>
          );
        })}
      {imageModalVisible && (
        <Modal size="big" showX={false} closeModal={closeImageModal}>
          <ImageGallery
            closeModal={closeImageModal}
            setCurrentImage={setCurrentImage}
            items={fullscreenImages}
            currentIndex={modalCurrentImage}
            isVertical={false}
          />
        </Modal>
      )}
    </MainContent>
  );
}

const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const MainContent = styled.div`
  animation: ${animateIn} 0.2s ease;
  max-width: ${({ theme }) => `${theme.grid.container.base}px`};
  width: 100%;
  margin: 0 auto;

  ${({ theme }) => theme.mediab.m580`
     padding: 0 ;
     margin-bottom: ${rem(16)};
  `}
`;

const CmsTitle = styled.h1<{ fromSitemap: boolean }>`
  margin: 0;
  margin-top: ${({ fromSitemap }) => (fromSitemap ? rem(24) : 0)};
  margin-bottom: ${rem(56)};
  font-weight: 400;
  font-size: ${rem(36)};
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: left;
  text-transform: uppercase;
  line-height: 47px;

  ${({ theme }) => theme.mediab.l925`
    margin-bottom: ${rem(36)};
  `}

  ${({ theme }) => theme.mediab.m580`
    font-size: ${rem(24)};
    line-height: 32px;
    margin-bottom: ${rem(24)};
  `}
`;

const ArticleInfoWrapper = styled(FlexRowCenterVertical)`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  width: 100%;
  flex: 1 1 100%;
`;

const titleLikeCss = css`
  font-family: ${({ theme }) => theme.font.secondary};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${rem(24)};
  text-transform: uppercase;
`;

const Body = styled.div`
  font-size: ${rem(16)};
  font-family: ${({ theme }) => theme.font.primary};
  font-weight: 300;
  line-height: ${rem(28)};
  color: ${({ theme }) => theme.colors.textPrimary};

  p {
    line-height: inherit;
  }

  img {
    max-height: 80vh;
    display: block;
    margin: 0 auto;
    margin-bottom: ${rem(24)};
  }

  a {
    font-weight: inherit;
    line-height: inherit;
    color: inherit;
    text-decoration: underline;

    &:hover,
    &:focus {
      color: inherit;
      text-decoration: underline;
    }
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    ${titleLikeCss};
  }

  h2 {
    line-height: ${rem(39)};
    font-size: ${rem(30)};

    ${({ theme }) => theme.mediab.m580`
      font-size: ${rem(24)};
      line-height: ${rem(32)};
    `}
  }

  h3 {
    line-height: ${rem(31)};
    font-size: ${rem(24)};

    ${({ theme }) => theme.mediab.m580`
      line-height: ${rem(26)};
      font-size: ${rem(20)};
    `}
  }

  h4 {
    line-height: ${rem(26)};
    font-size: ${rem(20)};

    ${({ theme }) => theme.mediab.m580`
      line-height: ${rem(23)};
      font-size: ${rem(18)};
    `}
  }

  h5 {
    line-height: ${rem(23)};
    font-size: ${rem(18)};

    ${({ theme }) => theme.mediab.m580`
      line-height: ${rem(20)};
      font-size: ${rem(16)};
    `}
  }

  h6 {
    line-height: ${rem(20)};
    font-size: ${rem(16)};
  }

  table {
    font-size: ${rem(16)};
    line-height: ${rem(24)};
    width: 100%;
    border: none;
    border-collapse: collapse;
    display: block;
    overflow-x: auto;
    white-space: nowrap;

    thead {
      background-color: ${({ theme }) => theme.colors.primary};
    }

    th {
      font-weight: 500;
      border-color: ${({ theme }) => rgba(theme.colors.white, 0.16)};
      color: ${({ theme }) => rgba(theme.colors.white, 1)};
      padding: ${rem(15)} ${rem(24)} ${rem(16)};
      text-align: left;
    }

    td {
      border: none;
      padding: ${rem(15)} ${rem(24)} ${rem(16)};
    }

    tbody {
      tr:nth-child(2n + 1) {
        background: ${({ theme }) => rgba(theme.colors.primary, 0.12)};
      }
    }
  }

  strong {
    font-weight: 500;
  }

  ul,
  ol {
    padding: 0;
    list-style: none;

    li {
      margin-bottom: ${rem(16)};
      line-height: ${rem(24)};
      position: relative;
    }
  }

  ol {
    counter-reset: ol-counter;

    li {
      counter-increment: ol-counter;

      ::before {
        content: counter(ol-counter) '.';
        display: inline-block;
        min-width: ${rem(30)};
        color: ${({ theme }) => rgba(theme.colors.primary, 1)};
        font-weight: 500;
      }
    }
  }

  ul {
    li {
      padding-left: ${rem(30)};

      ::before {
        content: '';
        display: block;
        position: absolute;
        width: ${rem(6)};
        height: ${rem(6)};
        border-radius: 50%;
        background: ${({ theme }) => rgba(theme.colors.primary, 1)};
        left: ${rem(8)};
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }
`;

const GalleryWrapper = styled(FlexRowCenterVertical)`
  margin: ${rem(48)} 0;
  flex-flow: row wrap;
`;

const Thumb = styled.img`
  width: ${rem(150)};
  height: ${rem(150)};
  margin-right: ${rem(16)};
  margin-bottom: ${rem(16)};
  cursor: pointer;
  border-radius: ${rem(4)};
`;

const MoreThumb = styled(FlexColCenter)`
  width: ${rem(80)};
  height: ${rem(80)};
  margin-right: ${rem(16)};
  margin-bottom: ${rem(16)};
  border: ${rem(1)} solid #ccc;
  cursor: pointer;
  border-radius: ${rem(4)};
  font-size: ${rem(32)};
  color: #ccc;
`;

const BackLink = styled(TextLink)`
  line-height: ${rem(18)};
  font-weight: 500;
  text-decoration: underline;

  display: block;
  margin-top: ${rem(32)};
  margin-bottom: ${rem(58)};

  &:hover,
  &:focus {
    text-decoration: underline;
  }

  svg {
    fill: inherit;
  }

  ${({ theme }) => theme.mediab.l925`
    margin-top: 0;
    margin-bottom: ${rem(24)};
  `}
`;

const smallTextLikeCss = css`
  font-size: ${rem(14)};
  line-height: ${rem(21)};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Date = styled(TextP)`
  ${smallTextLikeCss};
  font-weight: 400;
`;

const AuthorPhoto = styled.img`
  object-fit: cover;
  width: ${rem(56)};
  height: ${rem(56)};
  border-radius: 50%;
  max-width: initial;
  margin-right: ${rem(16)};
  flex: 0 0 auto;
`;

const InfoCover = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
  font-family: 'Open Sans', sans-serif;
`;

const AuthorName = styled.div`
  ${smallTextLikeCss};
  font-weight: 600;
  margin-bottom: ${rem(4)};
`;

const CreatedDate = styled.span`
  display: inline-block;
  margin-right: ${rem(8)};
`;

const LastUpdate = styled.span`
  display: inline-block;
  opacity: 0.56;
`;

const AuthorShareWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${rem(56)};
`;

const DownloadLink = styled.a`
  font-family: ${({ theme }) => theme.font.primary};
`;

export { Article };
